import React, { useEffect, useState } from "react";
import { req, tr, L } from "./api";
import { BiText, Field, Check, Msg } from "./fields";

const CATS = ["Company profile", "Practice sheet", "Brochure", "Report", "Media kit", "Other"];

export function DocumentsAdmin({ canEdit }) {
  const [items, setItems] = useState(null);
  const [file, setFile] = useState(null);
  const [meta, setMeta] = useState({ title: L(), description: L(), category: "Company profile", language: "en", access: "public", published: true });
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => req("/admin/documents").then(setItems).catch((e) => setMsg(e.message));
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) return;
    setBusy(true); setMsg("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", "document");
      fd.append("meta", JSON.stringify(meta));
      await req("/admin/upload", { method: "POST", body: fd });
      setMsg("Uploaded."); setFile(null); setMeta({ title: L(), description: L(), category: "Company profile", language: "en", access: "public", published: true });
      load();
    } catch (x) { setMsg("Could not upload: " + x.message); }
    setBusy(false);
  };
  const patch = async (id, p) => { try { await req(`/admin/documents/${id}`, { method: "PUT", body: p }); load(); } catch (x) { setMsg(x.message); } };
  const del = async (id) => { if (!window.confirm("Remove this document?")) return; await req(`/admin/files/documents/${id}`, { method: "DELETE" }); load(); };

  if (!items) return <p className="muted">Loading…</p>;
  return (
    <div className="stack" style={{ gap: 28 }}>
      <Msg msg={msg} />
      {canEdit && (
        <form className="form" onSubmit={upload} data-testid="doc-upload-form">
          <div className="grp">Upload a document</div>
          <Field label="File (PDF, DOCX, XLSX, PPTX — up to 25 MB)" full><input type="file" accept=".pdf,.docx,.xlsx,.pptx" onChange={(e) => setFile(e.target.files[0])} data-testid="doc-file" /></Field>
          <BiText label="Title" value={meta.title} onChange={(v) => setMeta({ ...meta, title: v })} />
          <BiText label="Description" value={meta.description} onChange={(v) => setMeta({ ...meta, description: v })} />
          <Field label="Category"><select value={meta.category} onChange={(e) => setMeta({ ...meta, category: e.target.value })}>{CATS.map((c) => <option key={c}>{c}</option>)}</select></Field>
          <Field label="Language"><select value={meta.language} onChange={(e) => setMeta({ ...meta, language: e.target.value })}><option value="en">English</option><option value="bn">বাংলা</option><option value="both">Both</option></select></Field>
          <Field label="Access"><select value={meta.access} onChange={(e) => setMeta({ ...meta, access: e.target.value })} data-testid="doc-access"><option value="public">Public download</option><option value="request">Shared on request</option><option value="private">Private (admin only)</option></select></Field>
          <div className="row-actions" style={{ gridColumn: "1/-1" }}><button className="btn" type="submit" disabled={busy || !file} data-testid="doc-upload-btn">{busy ? "Uploading…" : "Upload"}</button></div>
        </form>
      )}
      {items.length ? (
        <div className="tbl-wrap"><table className="tbl" data-testid="documents-table">
          <thead><tr><th>Document</th><th>Category</th><th>Access</th><th>Downloads</th><th></th></tr></thead>
          <tbody>{items.map((d) => (
            <tr key={d.id}>
              <td><strong>{tr(d.title) || d.filename}</strong>{!tr(d.title, "bn") && <div className="bn-miss">Bangla missing</div>}<div className="muted small">{d.filename} · {(d.size / 1024).toFixed(0)} KB</div></td>
              <td>{d.category}</td>
              <td><select value={d.access} onChange={(e) => patch(d.id, { access: e.target.value })} disabled={!canEdit}><option value="public">Public</option><option value="request">On request</option><option value="private">Private</option></select></td>
              <td className="num">{d.downloads || 0}</td>
              <td><div className="row-actions">
                <a className="btn sm ghost" href={`/api/files/${d.storagePath}`} target="_blank" rel="noopener noreferrer">Open</a>
                <button type="button" className="btn sm ghost" onClick={() => patch(d.id, { published: d.published === false })}>{d.published === false ? "Publish" : "Unpublish"}</button>
                {canEdit && <button className="btn sm ghost danger" onClick={() => del(d.id)}>Delete</button>}
              </div></td>
            </tr>))}</tbody></table></div>
      ) : <div className="empty">No documents yet.</div>}
    </div>
  );
}

export function MediaAdmin({ canEdit }) {
  const [items, setItems] = useState(null);
  const [file, setFile] = useState(null);
  const [alt, setAlt] = useState(L());
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => req("/admin/media").then(setItems).catch((e) => setMsg(e.message));
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) return;
    setBusy(true); setMsg("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", "media");
      fd.append("meta", JSON.stringify({ alt }));
      await req("/admin/upload", { method: "POST", body: fd });
      setMsg("Uploaded."); setFile(null); setAlt(L()); load();
    } catch (x) { setMsg("Could not upload: " + x.message); }
    setBusy(false);
  };
  const del = async (id) => { if (!window.confirm("Remove this image?")) return; await req(`/admin/files/media/${id}`, { method: "DELETE" }); load(); };

  if (!items) return <p className="muted">Loading…</p>;
  return (
    <div className="stack" style={{ gap: 28 }}>
      <Msg msg={msg} />
      {canEdit && (
        <form className="form" onSubmit={upload} data-testid="media-upload-form">
          <div className="grp">Upload a photograph</div>
          <Field label="Image (JPG, PNG, WebP — up to 15 MB)" full><input type="file" accept=".jpg,.jpeg,.png,.webp,.heic" onChange={(e) => setFile(e.target.files[0])} data-testid="media-file" /></Field>
          <BiText label="Alt text (required)" value={alt} onChange={setAlt} />
          <div className="row-actions" style={{ gridColumn: "1/-1" }}><button className="btn" type="submit" disabled={busy || !file || !alt.en} data-testid="media-upload-btn">{busy ? "Uploading…" : "Upload"}</button></div>
        </form>
      )}
      {items.length ? (
        <div className="mediagrid" data-testid="media-grid">
          {items.map((m) => (
            <figure key={m.id}>
              <img src={`/api/files/${m.storagePath}`} alt={tr(m.alt)} loading="lazy" />
              <figcaption>{m.filename}</figcaption>
              <figcaption className="row-actions">
                <button type="button" className="btn sm ghost" onClick={() => { navigator.clipboard?.writeText(`/api/files/${m.storagePath}`); }}>Copy URL</button>
                {canEdit && <button type="button" className="btn sm ghost danger" onClick={() => del(m.id)}>Delete</button>}
              </figcaption>
            </figure>))}
        </div>
      ) : <div className="empty">No photographs uploaded yet.</div>}
    </div>
  );
}
